import { RECORDED_GRAPH_WRITE_ADVISORY_LOCK_NAMESPACE } from "../../backend/advisory-lock-namespaces";
import { recordedRevisionOriginsMembers } from "../../backend/capabilities/bind";
import { type RECORDED_REVISION_ORIGINS } from "../../backend/capabilities/bundle-registry";
import { type BundleVerdictOf } from "../../backend/capabilities/resolve";
import {
  type FenceStatements,
  requireWriteFence,
  resolveFenceStatements,
  resolveWriteFencePlan,
} from "../../backend/capabilities/write-fence";
import {
  mintGraphCommandCoordination,
  normalizeGraphCommandIsolation,
} from "../../backend/command-contract";
import { postgresFenceSql } from "../../backend/drizzle/postgres-fence-sql";
import type {
  GraphBackend,
  GraphCommandCoordination,
  GraphCommandIsolation,
} from "../../backend/types";
import {
  createRecordedInstant,
  parseRecordedInstant,
  RECORDED_MAX_REVISION,
  type RecordedInstant,
} from "../../core/temporal";
import { ConfigurationError } from "../../errors";
import { type SqlSchema } from "../../query/compiler/schema";
import { sql, type SqlFragment } from "../../query/sql-fragment";
import { asCompiledRowsSql } from "../../query/sql-intent";
import { canonicalizeDatabaseTimestamp, nowIso } from "../../utils/date";
import { generateId } from "../../utils/id";
import { executeStatement } from "./guards";

type ClockRow = Readonly<{ recorded_at: unknown; revision: unknown }>;
type RecordedClockParts = Readonly<{ recordedAt: string; revision: number }>;
export type AllocatedRecordedCommit = RecordedClockParts &
  Readonly<{ instant: string }>;
type RevisionOriginRow = Readonly<{ origin: unknown }>;

type RecordedClockBackend = Pick<
  GraphBackend,
  "capabilities" | "dialect" | "execute" | "executeStatement"
>;

type RevisionOriginBackend = Pick<
  GraphBackend,
  "dialect" | "ensureRevisionOriginsTable" | "execute" | "executeStatement"
>;

/**
 * Floor sentinel used only to seed-and-lock a graph's clock row before the
 * read/advance/write sequence on SQLite-family backends. Always overwritten by
 * the real commit within the same transaction. Revision zero is reserved for
 * this internal row and is never exposed as a valid recorded instant.
 */
const RECORDED_MIN_REVISION = 0;
const RECORDED_MIN_TIME = "1970-01-01T00:00:00.000Z";
const RECORDED_CLOCK_ADVISORY_LOCK_NAMESPACE = "typegraph:recorded-clock";

/**
 * `resolveFenceStatements(postgresFenceSql)`, hoisted once: the bundled
 * PostgreSQL spelling's derived standalone-statement forms, for the two
 * functions below that render this module's advisory locks' actual SQL text
 * for a caller that needs it directly rather than through a resolved plan —
 * a test driving a raw connection, or a competing manual lock.
 */
const postgresFenceStatements = resolveFenceStatements(postgresFenceSql);

/**
 * Renders the bundled PostgreSQL spelling of this module's two advisory
 * locks, for a caller that needs the actual SQL text rather than a resolved
 * plan — a test driving a raw connection, or a competing manual lock. The
 * lock SITES themselves (`lockRecordedClock`, `acquireRecordedGraphWriteLock`)
 * never call these: they consume `fence.sql.*` from their own resolved
 * {@link resolveWriteFencePlan}, so a backend with a different lock spelling
 * is honored instead of overridden by this module's PostgreSQL default.
 *
 * Keep lock namespaces tied to acquire order, not to feature names:
 *
 * - recorded graph writes take `typegraph:recorded-graph-write` before any row
 *   reads/writes that can affect graph state;
 * - recorded-clock allocation takes `typegraph:recorded-clock` late, at flush,
 *   after the live writes have already happened.
 *
 * Sharing one key across those two acquire-order positions creates a circular
 * wait under ordinary concurrent load.
 */
export function recordedClockAdvisoryLockSql(graphId: string): SqlFragment {
  return postgresFenceStatements.acquireKeyed(
    RECORDED_CLOCK_ADVISORY_LOCK_NAMESPACE,
    graphId,
  );
}

export function recordedGraphWriteAdvisoryLockSql(
  graphId: string,
): SqlFragment {
  return postgresFenceStatements.acquireKeyedWithIsolation(
    RECORDED_GRAPH_WRITE_ADVISORY_LOCK_NAMESPACE,
    graphId,
  );
}

/**
 * Compile-time evidence that the per-graph write-lock discipline was
 * satisfied BEFORE any row work in the current transaction — either the
 * advisory lock was actually acquired ({@link lockRecordedGraphWrite}, on a
 * capture-enabled Postgres store) or the store provably needs no lock
 * ({@link uncapturedGraphWriteLock}). Functions that perform captured row
 * writes (the node write pipeline) require this token as a parameter, so
 * "sidecar write before lock" is a compile error rather than a lock-order
 * inversion found in review.
 */
declare const GRAPH_WRITE_LOCK_BRAND: unique symbol;

export type GraphWriteLock = Readonly<{
  [GRAPH_WRITE_LOCK_BRAND]: true;
  coordination: GraphCommandCoordination | undefined;
}>;

const UNCOORDINATED_GRAPH_WRITE_LOCK = Object.freeze({
  coordination: undefined,
}) as GraphWriteLock;

function acquiredGraphWriteLock(
  target: Pick<GraphBackend, "commands">,
  graphId: string,
  isolation: GraphCommandIsolation,
): GraphWriteLock {
  return Object.freeze({
    coordination: mintGraphCommandCoordination(
      target.commands,
      graphId,
      isolation,
    ),
  }) as GraphWriteLock;
}

/** Record an advisory lock acquired by a combined backend primitive. */
export function acquiredGraphWriteLockFromCombinedFence(
  target: Pick<GraphBackend, "commands">,
  graphId: string,
  isolation: GraphCommandIsolation,
): GraphWriteLock {
  return acquiredGraphWriteLock(target, graphId, isolation);
}

/**
 * Evidence constructor for stores WITHOUT history capture, where no
 * advisory lock exists to acquire. Calling this is an explicit claim that
 * the target store is not capture-enabled — do not use it to skip the lock
 * on a history store.
 */
export function uncapturedGraphWriteLock(): GraphWriteLock {
  return UNCOORDINATED_GRAPH_WRITE_LOCK;
}

/**
 * Per-transaction memo of graphs whose advisory lock is already held.
 *
 * `pg_advisory_xact_lock` is reentrant and held until the top-level
 * transaction ends, so re-acquiring it on every captured write inside one
 * transaction is pure round-trip churn — a multi-write transaction paid one
 * lock round trip per write. The capture layer registers its per-transaction
 * backend here; every lock path that receives that backend (the capture
 * delegate's own writes, `runInWriteTransaction`, provenance) then skips the
 * `SqlFragment` once the graph's lock is held.
 *
 * Keyed weakly by the backend object: the delegate is created per transaction,
 * so memo lifetime equals lock lifetime. The recorded savepoint coordinator
 * checkpoints and restores this memo with captured touches; manual savepoints
 * remain outside the capture contract.
 *
 * Single-flight per graph: the memo stores the IN-FLIGHT acquisition
 * promise, not just completed acquisitions, so concurrent same-transaction
 * writers (`Promise.all` over captured writes) coalesce onto one advisory
 * round trip instead of racing past an empty resolved-set. A rejected
 * acquisition evicts its entry so a retry is not poisoned (in practice a
 * failed statement has aborted the Postgres transaction anyway).
 */
export type RecordedGraphLockMemo = Map<string, Promise<GraphCommandIsolation>>;

export function createRecordedGraphLockMemo(): RecordedGraphLockMemo {
  return new Map();
}

const recordedGraphLockMemos = new WeakMap<object, RecordedGraphLockMemo>();
/**
 * Seeds the registered single-flight memo after another statement acquired the
 * same transaction-scoped graph lock.
 *
 * The combined schema/graph fence is the only caller: it invokes this only
 * after its statement succeeds. An existing entry is never replaced — it may
 * be an in-flight acquisition whose rejection must retain the ordinary
 * eviction/error semantics.
 */
export function memoizeAcquiredRecordedGraphWriteLock(
  backend: object,
  graphId: string,
  isolation: GraphCommandIsolation,
): void {
  const memo = recordedGraphLockMemos.get(backend);
  if (memo === undefined || memo.has(graphId)) return;
  memo.set(graphId, Promise.resolve(isolation));
}

export function registerRecordedGraphLockMemo(
  backend: object,
  memo: RecordedGraphLockMemo,
): void {
  recordedGraphLockMemos.set(backend, memo);
}

async function acquireRecordedGraphWriteLock(
  target: Pick<GraphBackend, "execute">,
  fenceSql: FenceStatements,
  graphId: string,
): Promise<GraphCommandIsolation> {
  const rows = await target.execute<
    Readonly<{ transaction_isolation: unknown }>
  >(
    asCompiledRowsSql(
      fenceSql.acquireKeyedWithIsolation(
        RECORDED_GRAPH_WRITE_ADVISORY_LOCK_NAMESPACE,
        graphId,
      ),
    ),
  );
  return normalizeGraphCommandIsolation(rows[0]?.transaction_isolation);
}

export async function lockRecordedGraphWrite(
  target: Pick<
    GraphBackend,
    "capabilities" | "commands" | "dialect" | "execute"
  >,
  graphId: string,
  memo?: RecordedGraphLockMemo,
): Promise<GraphWriteLock> {
  const plan = resolveWriteFencePlan(target);
  const fence = requireWriteFence(plan, "recorded graph write", "keyed");
  switch (fence.kind) {
    case "engine-serialized":
    case "caller-serialized": {
      // A writer slot — or, under `caller-serialized`, the deployment's own
      // serialization promise — can serialize ordinary row work, but
      // neither is an advisory acquisition bound to this graph/port. It
      // cannot authorize the PostgreSQL convergence command.
      return uncapturedGraphWriteLock();
    }
    case "lock":
    case "row": {
      break;
    }
    default: {
      fence satisfies never;
    }
  }
  const effectiveMemo = memo ?? recordedGraphLockMemos.get(target);
  if (effectiveMemo === undefined) {
    const isolation = await acquireRecordedGraphWriteLock(
      target,
      fence.sql,
      graphId,
    );
    return acquiredGraphWriteLock(target, graphId, isolation);
  }
  let pending = effectiveMemo.get(graphId);
  if (pending === undefined) {
    pending = acquireRecordedGraphWriteLock(target, fence.sql, graphId).catch(
      (error: unknown) => {
        effectiveMemo.delete(graphId);
        throw error;
      },
    );
    effectiveMemo.set(graphId, pending);
  }
  const isolation = await pending;
  return acquiredGraphWriteLock(target, graphId, isolation);
}

function failInvalidClock(value: unknown, cause?: unknown): never {
  throw new ConfigurationError(
    "Recorded clock row contained an invalid revision or wall time",
    { value },
    {
      cause,
      suggestion:
        "Run migrateLegacyRecordedTime() before using recorded-time tables created by the timestamp-only preview schema.",
    },
  );
}

function recordedClockRevision(value: unknown): number {
  const revision =
    typeof value === "bigint" ? Number(value)
    : typeof value === "string" && /^\d+$/.test(value) ? Number(value)
    : value;
  if (
    typeof revision !== "number" ||
    !Number.isSafeInteger(revision) ||
    revision < RECORDED_MIN_REVISION ||
    revision >= RECORDED_MAX_REVISION
  ) {
    return failInvalidClock(value);
  }
  return revision;
}

function recordedClockWallTime(value: unknown): string {
  return canonicalizeDatabaseTimestamp(value) ?? failInvalidClock(value);
}

function recordedClockParts(row: ClockRow): RecordedClockParts | undefined {
  const revision = recordedClockRevision(row.revision);
  if (revision === RECORDED_MIN_REVISION) return undefined;
  return { revision, recordedAt: recordedClockWallTime(row.recorded_at) };
}

function nextRecordedCommitParts(
  previous: RecordedClockParts | undefined,
): RecordedClockParts {
  const wallTime = nowIso();
  if (previous === undefined) {
    return { revision: 1, recordedAt: wallTime };
  }

  return {
    revision: previous.revision + 1,
    // Keep diagonal replay cumulative across backward clock corrections without
    // manufacturing a new millisecond for same-ms commits. Throughput can make
    // this component repeat, never run ahead of the greatest observed wall time.
    recordedAt: nonDecreasingWallTime(wallTime, previous.recordedAt),
  };
}

function nonDecreasingWallTime(current: string, previous: string): string {
  if (current < previous) return previous;
  return current;
}

/**
 * Reads the recorded-time high-water mark for a graph — the latest committed
 * recorded instant. Exported so {@link Store.recordedNow} can hand callers a
 * deterministic `asOfRecorded` anchor. Accepts any read-capable backend or
 * transaction.
 */
export async function readRecordedClock(
  target: Pick<GraphBackend, "execute">,
  schema: SqlSchema,
  graphId: string,
): Promise<RecordedInstant | undefined> {
  const parts = await readRecordedClockParts(target, schema, graphId);
  return parts === undefined ? undefined : (
      createRecordedInstant(parts.revision, parts.recordedAt)
    );
}

async function readRecordedClockParts(
  target: Pick<GraphBackend, "execute">,
  schema: SqlSchema,
  graphId: string,
): Promise<RecordedClockParts | undefined> {
  const rows = await target.execute<ClockRow>(
    asCompiledRowsSql(sql`
      SELECT revision, recorded_at
      FROM ${schema.recordedClockTable}
      WHERE graph_id = ${graphId}
    `),
  );
  const row = rows[0];
  return row === undefined ? undefined : recordedClockParts(row);
}

/**
 * Reads the durable, random origin assigned to one graph's revision clock.
 * The origin namespaces an otherwise graph-local revision anchor, so a
 * branch cannot mistake another store's coincident clock value for its base.
 */
export async function readRevisionOrigin(
  target: Pick<GraphBackend, "execute">,
  schema: SqlSchema,
  graphId: string,
): Promise<string | undefined> {
  const rows = await target.execute<RevisionOriginRow>(
    asCompiledRowsSql(sql`
      SELECT origin
      FROM ${schema.revisionOriginsTable}
      WHERE graph_id = ${graphId}
    `),
  );
  const first = rows[0];
  if (first === undefined) return undefined;
  if (typeof first.origin !== "string" || first.origin.length === 0) {
    throw new ConfigurationError(
      "Revision origin row contained an invalid origin",
      { graphId, origin: first.origin },
    );
  }
  return first.origin;
}

/**
 * Ensures the origins relation exists and refuses a backend that cannot
 * bootstrap it. Runs the same `ensureRevisionOriginsTable` DDL member
 * {@link ensureRevisionOrigin} bootstraps from — exported separately (rather
 * than folded into a single ensure-and-write call) because that member is a
 * schema-DDL operation, never projected onto an open `transaction()` handle:
 * a caller that needs a row change inside a transaction (`Store.clear()`
 * pairing this with {@link resetRevisionOrigin}) must ensure the table on
 * the ROOT backend, BEFORE opening that transaction, then touch the row
 * inside it.
 */
export async function ensureRevisionOriginsRelation(
  target: RevisionOriginBackend,
  verdict: BundleVerdictOf<typeof RECORDED_REVISION_ORIGINS>,
): Promise<void> {
  if (!verdict.supported) {
    throw new ConfigurationError(
      "Revision tracking requires a backend that can bootstrap revision origins.",
      { dialect: target.dialect },
      {
        suggestion:
          "Use a built-in SQLite/PostgreSQL backend, or implement ensureRevisionOriginsTable on the custom backend.",
      },
    );
  }
  if (ENSURED_ORIGINS_RELATIONS.has(target)) return;
  const { ensureRevisionOriginsTable: ensureTable } =
    recordedRevisionOriginsMembers(target, verdict);
  await ensureTable();
  ENSURED_ORIGINS_RELATIONS.add(target);
}

/**
 * Backends whose origins relation this process has already ensured. The
 * relation is DDL (`CREATE TABLE IF NOT EXISTS`) and a table never goes
 * away once created, so ensuring it once per backend object is enough; the
 * origin ROW is read fresh on every call because `Store.clear()` rotates
 * it, and that freshness is what callers of {@link ensureRevisionOrigin}
 * pay for — not a DDL round trip per mint.
 */
const ENSURED_ORIGINS_RELATIONS = new WeakSet<object>();

/**
 * Returns a graph's durable revision-origin nonce, creating it exactly once.
 * The unique graph-id row makes concurrent first readers converge on the
 * winner's origin rather than manufacturing incompatible anchors.
 */
export async function ensureRevisionOrigin(
  target: RevisionOriginBackend,
  verdict: BundleVerdictOf<typeof RECORDED_REVISION_ORIGINS>,
  schema: SqlSchema,
  graphId: string,
): Promise<string> {
  await ensureRevisionOriginsRelation(target, verdict);
  const existing = await readRevisionOrigin(target, schema, graphId);
  if (existing !== undefined) return existing;

  await executeStatement(
    target,
    sql`
      INSERT INTO ${schema.revisionOriginsTable} (graph_id, origin)
      VALUES (${graphId}, ${generateId()})
      ON CONFLICT (graph_id) DO NOTHING
    `,
  );
  const origin = await readRevisionOrigin(target, schema, graphId);
  if (origin !== undefined) return origin;
  throw new ConfigurationError(
    "Revision origin was not persisted after initialization.",
    { graphId, dialect: target.dialect },
  );
}

/**
 * Deletes a graph's durable revision-origin row so the next
 * {@link ensureRevisionOrigin} call mints a fresh nonce. Row-write only
 * (`execute`/`executeStatement`, the same narrow surface
 * {@link readRecordedClock} needs) — deliberately NOT the DDL-capable
 * {@link RevisionOriginBackend} `ensureRevisionOrigin` takes, so this is
 * safe to call on an open `transaction()` handle. A caller whose origins
 * table might not exist yet must call {@link ensureRevisionOriginsRelation}
 * on the root backend first (`Store.clear()` does, before opening its
 * transaction).
 *
 * `Store.clear()` calls this inside the same transaction as `clearGraph`:
 * the recorded clock (and, for a non-capturing revision-tracked store, its
 * immediate reseed) restarts numbering from the same low values every
 * clear, so leaving the origin row untouched would let a graph repopulated
 * to the same revision COUNT after a clear silently reconstruct the exact
 * `base@V` anchor a pre-clear branch forked from — schema half unchanged,
 * origin unchanged, revision numbering coincidentally realigned. Rotating
 * the origin here is what makes that recurrence impossible: every mint
 * after this point draws a new random nonce, so no later anchor can equal
 * one minted before the clear.
 */
export async function resetRevisionOrigin(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
): Promise<void> {
  await executeStatement(
    target,
    sql`
      DELETE FROM ${schema.revisionOriginsTable}
      WHERE graph_id = ${graphId}
    `,
  );
}

/**
 * Persists a graph's clock row, SELF-FENCED against a backward move.
 *
 * The per-graph revision is the total order every recorded instant is read
 * back in, so lowering it re-issues instants that were already handed out —
 * silent corruption of recorded-time ordering, not a loud failure. The row
 * therefore refuses the write itself instead of trusting whoever computed the
 * value: the conflict update applies only when the proposed revision strictly
 * advances the stored one. A caller that read the clock outside the lock and
 * lost the race becomes a no-op here (and is refused by
 * {@link assertRecordedClockAdvanced}) rather than a rollback of the clock.
 *
 * One predicate for both dialects: SQLite and PostgreSQL share the
 * `ON CONFLICT ... DO UPDATE ... WHERE` form and the `excluded.` alias for the
 * proposed row. The stored row is qualified by table name so neither engine has
 * to resolve a bare `revision` against two candidate rows.
 */
async function writeRecordedClock(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
  parts: RecordedClockParts,
): Promise<void> {
  await executeStatement(
    target,
    sql`
      INSERT INTO ${schema.recordedClockTable} (graph_id, revision, recorded_at)
      VALUES (${graphId}, ${parts.revision}, ${parts.recordedAt})
      ON CONFLICT (graph_id) DO UPDATE
      SET revision = excluded.revision, recorded_at = excluded.recorded_at
      WHERE ${schema.recordedClockTable}.revision < excluded.revision
    `,
  );
}

/**
 * Confirms the fenced UPSERT actually took the allocated revision.
 *
 * Runs only on the `previousRevision` path — the one where the caller, not this
 * module, supplied the clock's previous value. There the fence alone is not
 * enough: it protects the ROW, but the allocated instant has already been
 * computed from the caller's claim and would be returned as if committed,
 * stamping rows with an instant the clock never reached. An allocation that the
 * row declined is refused here instead of reported.
 *
 * WHY THE OTHER ARM NEEDS NO RE-READ, and why that is a property rather than an
 * assumption: when `previousRevision` is absent the value is read by
 * {@link readRecordedClockParts} AFTER {@link lockRecordedClock}, inside the
 * same transaction that then writes — a real fence on both dialects
 * (`pg_advisory_xact_lock`; SQLite's seed-UPSERT or the enclosing
 * `BEGIN IMMEDIATE`). Those constructs are transaction-scoped, so the fence
 * would be vacuous on a backend with no transactions — but such a backend can
 * never reach here: `assertRevisionTrackableBackend` refuses
 * `revisionTracking` (and therefore `history`, which implies it) at Store
 * construction unless `capabilities.execution.interactiveTransactions` is set. Adding the re-read to
 * that arm would put an unfalsifiable extra SELECT on every captured write.
 */
async function assertRecordedClockAdvanced(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
  revision: number,
): Promise<void> {
  const stored = await readRecordedClockParts(target, schema, graphId);
  if (stored?.revision === revision) return;
  throw new ConfigurationError(
    "Recorded commit allocation was refused: the supplied previous revision was stale, so the write would have moved the graph's revision clock backward.",
    { graphId, allocatedRevision: revision, storedRevision: stored?.revision },
    {
      suggestion:
        "Read the previous revision inside the same transaction that allocates the commit (omit previousRevision), or hold the per-graph write lock across the read and the allocation.",
    },
  );
}

async function lockRecordedClock(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
  ownsWriteLock: boolean,
): Promise<void> {
  // Serialize the read/advance/write sequence per graph. Without this,
  // concurrent transactions can read the same previous clock value and
  // allocate the same recorded instant.
  const plan = resolveWriteFencePlan(target);
  const fence = requireWriteFence(plan, "recorded clock allocation", "keyed");
  switch (fence.kind) {
    case "lock":
    case "row": {
      await target.execute(
        asCompiledRowsSql(
          fence.sql.acquireKeyed(
            RECORDED_CLOCK_ADVISORY_LOCK_NAMESPACE,
            graphId,
          ),
        ),
      );
      return;
    }
    case "engine-serialized": {
      // SQLite: the seed-UPSERT exists only to take the clock row's write lock
      // before reading when the enclosing transaction did NOT already hold one.
      // Bundled transactions open BEGIN IMMEDIATE, so the lock is already held.
      if (ownsWriteLock) return;
      await executeStatement(
        target,
        sql`
          INSERT INTO ${schema.recordedClockTable} (graph_id, revision, recorded_at)
          VALUES (${graphId}, ${RECORDED_MIN_REVISION}, ${RECORDED_MIN_TIME})
          ON CONFLICT (graph_id) DO UPDATE SET revision = revision
        `,
      );
      return;
    }
    case "caller-serialized": {
      // Unlike `engine-serialized`, this is never conditional on
      // `ownsWriteLock`: the seed-UPSERT above exists to take a row-level
      // write lock SQLite's `BEGIN IMMEDIATE` might not already hold, which
      // is a proof about that one engine's transaction, not about this
      // declaration. `caller-serialized` is the deployment's own promise
      // that no other client writes to this database at all, so there is no
      // concurrent writer for a seed row to exclude regardless of which
      // transaction opened this one.
      return;
    }
    default: {
      fence satisfies never;
    }
  }
}

/**
 * Narrows a parsed `previousRevision` to TypeGraph's own numeric clock shape.
 * This module allocates ONLY TypeGraph-owned commits — an engine-native
 * backend never touches a TypeGraph clock at all, so it never reaches
 * `allocateRecordedCommit` — so a caller-supplied `previousRevision` that
 * parses as an engine-native (`e1`) anchor can only be a caller mistake.
 */
function requireTypeGraphClockParts(
  parts: ReturnType<typeof parseRecordedInstant>,
): RecordedClockParts {
  if (parts.kind !== "typegraph") {
    throw new ConfigurationError(
      "allocateRecordedCommit's previousRevision must be a TypeGraph-owned (r1) recorded instant.",
      { revisionKind: parts.kind },
    );
  }
  return parts;
}

export async function allocateRecordedCommit(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
  ownsWriteLock: boolean,
  previousRevision?: string,
): Promise<AllocatedRecordedCommit> {
  await lockRecordedClock(target, schema, graphId, ownsWriteLock);
  const previous =
    previousRevision === undefined ?
      await readRecordedClockParts(target, schema, graphId)
    : requireTypeGraphClockParts(
        parseRecordedInstant(previousRevision, "previous recorded revision"),
      );
  const { revision, recordedAt } = nextRecordedCommitParts(previous);
  if (revision >= RECORDED_MAX_REVISION) {
    throw new ConfigurationError(
      `Recorded commit clock reached the open revision sentinel ${RECORDED_MAX_REVISION}`,
      { graphId, revision },
      {
        suggestion:
          "Start a new graph before exhausting the per-graph recorded revision space.",
      },
    );
  }
  const instant = createRecordedInstant(revision, recordedAt);
  await writeRecordedClock(target, schema, graphId, { revision, recordedAt });
  if (previousRevision !== undefined) {
    await assertRecordedClockAdvanced(target, schema, graphId, revision);
  }
  return { instant, revision, recordedAt };
}

/**
 * Advances the durable graph revision after a successful live-store write.
 *
 * Revision tracking deliberately reuses the monotonic per-graph revision already
 * owned by recorded-time capture. The write path holds the graph write lock
 * before this runs. Callers additionally state whether their SQLite
 * transaction owns the write lock so `allocateRecordedCommit` can skip its
 * redundant seed-UPSERT only on bundled `BEGIN IMMEDIATE` paths. It preserves
 * monotonicity independently of wall-clock behavior. History capture calls
 * `allocateRecordedCommit` during its own flush instead, so a captured write
 * remains one revision rather than being advanced twice.
 */
export async function advanceRevisionClock(
  target: RecordedClockBackend,
  schema: SqlSchema,
  graphId: string,
  ownsWriteLock: boolean,
  previousRevision?: string,
): Promise<string> {
  const commit = await allocateRecordedCommit(
    target,
    schema,
    graphId,
    ownsWriteLock,
    previousRevision,
  );
  return commit.instant;
}
